/**
 * SidebarFooter — user identity + upgrade CTA + account menu trigger.
 *
 * Users with a mentorship role already have coaching access so we hide
 * the Upgrade button for them. Users with both Education + Networking
 * access don't need it either.
 *
 * Phase E1: extracted from Sidebar.tsx.
 */

import { forwardRef } from 'react';
import { ChevronRight } from 'lucide-react';
import TouchTooltip from '../../ui/TouchTooltip';
import { type BackendUserWithPlan, hasMentorshipRole } from '../../../userService';
import type { TabType } from '../../navigation/tabMetadata';

type Props = {
    userData: BackendUserWithPlan | null;
    displayName: string;
    initials: string;
    userRole: string;
    activeTab: TabType;
    collapsed: boolean;
    userMenuOpen: boolean;
    onProfileTab: () => void;
    onOpenMenu: () => void;
    onUpgrade: () => void;
};

const SidebarFooter = forwardRef<HTMLButtonElement, Props>(function SidebarFooter({
    userData, displayName, initials, userRole, activeTab, collapsed,
    userMenuOpen, onProfileTab, onOpenMenu, onUpgrade,
}, anchorRef) {
    const shouldShowUpgrade = !hasMentorshipRole(userData)
        && !(userData?.access?.education && userData?.access?.networking);

    const userButton = (
        <button
            type="button"
            className={`user-section ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => {
                if (collapsed) onOpenMenu();
                else onProfileTab();
            }}
            aria-label={collapsed ? displayName : undefined}
            ref={collapsed ? anchorRef : undefined}
        >
            <div className="user-avatar">
                <span>{initials}</span>
            </div>
            {!collapsed && (
                <div className="user-info">
                    <span className="user-name">{displayName}</span>
                    <span className="user-role">{userRole}</span>
                </div>
            )}
        </button>
    );

    return (
        <div className="sidebar-footer">
            <div className="user-section-wrapper">
                {collapsed ? (
                    <TouchTooltip label={displayName} placement="right">
                        {userButton}
                    </TouchTooltip>
                ) : userButton}

                {!collapsed && (
                    <>
                        {shouldShowUpgrade && (
                            <TouchTooltip label="Upgrade plan" placement="top" delayMs={500}>
                                <button
                                    type="button"
                                    className="upgrade-btn"
                                    aria-label="Upgrade plan"
                                    onClick={onUpgrade}
                                >
                                    <span>Upgrade</span>
                                </button>
                            </TouchTooltip>
                        )}
                        <button
                            type="button"
                            className="user-menu-trigger"
                            aria-label="Open account menu"
                            aria-haspopup="menu"
                            {...(userMenuOpen ? { 'aria-expanded': true } : { 'aria-expanded': false })}
                            ref={anchorRef}
                            onClick={onOpenMenu}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
});

export default SidebarFooter;
